/**
 * About. Short, because the product pages already make the argument — this is
 * for the reader who wants to know what the thing believes before they trust it
 * with a training record.
 */

import { Link } from 'react-router-dom';
import { DocSection, PublicPage } from './PublicPage.js';

export function About(): JSX.Element {
  return (
    <PublicPage
      title="About FlightSlots"
      lede="Scheduling built around the constraints flight training actually has, rather than a calendar with aircraft written in the title."
    >
      <DocSection title="Why it exists">
        <p>
          A flight lesson is not a meeting. It needs an airworthy aircraft, an instructor inside a
          defensible duty window, a student whose endorsements are current, and under Part 141 a
          syllabus position that permits the lesson at all. Those four facts are usually held by four
          different people and checked together by none of them, which is why a double-booked
          airplane gets discovered on the ramp.
        </p>
        <p>
          FlightSlots checks them when the slot is offered rather than after the flight is booked.
          That is the whole idea; everything else is consequence.
        </p>
      </DocSection>

      <DocSection title="What it will not do">
        <p>
          It will not tell you a flight is legal. It reports what needs review, with the fact and the
          regulation behind each finding, and the pilot in command decides. A tool that says
          &ldquo;cleared&rdquo; invites someone to stop looking, and the day it is wrong it is wrong
          about an airplane. That constraint is in the <Link to="/terms#advisory">terms</Link>, not
          just in the marketing.
        </p>
      </DocSection>

      <DocSection title="How it is built">
        <p>
          One machine, one disk, one SQLite database, backed up continuously to object storage. It is
          deliberately not horizontally scaled: the schedule is a single file with a single writer,
          and a second instance would quietly get its own copy of the school. The trade-offs are
          described honestly on the <Link to="/security">security page</Link> rather than hidden
          behind the word &ldquo;cloud&rdquo;.
        </p>
        <p>
          It is designed for one CFI first. A freelance instructor with a single rented aircraft can
          run an entire practice from the day view, and a Part 141 program with a fleet uses the same
          grid with more of it turned on.
        </p>
      </DocSection>

      <DocSection title="Talk to us">
        <p>
          Questions about a school or a 141 program go through the{' '}
          <Link to="/contact?about=sales">contact form</Link>. Help with an account is in the{' '}
          <Link to="/support">help centre</Link>. Or{' '}
          <Link to="/signup">start a free school</Link> and look around first — that tells you more
          than we can.
        </p>
      </DocSection>
    </PublicPage>
  );
}
